import React, { useLayoutEffect, useRef, useState } from "react";
import { getBezierPath, getBezierEdgeCenter } from "react-flow-renderer";

const MARGEN_EXTREMOS = 24;

const ALTURA_ARCO = 60;

const aCoordenadasFlow = (evento, ctmInversa, svg) => {
  const punto = svg.createSVGPoint();
  punto.x = evento.clientX;
  punto.y = evento.clientY;
  const transformado = punto.matrixTransform(ctmInversa);
  return { x: Math.round(transformado.x), y: Math.round(transformado.y) };
};

const distanciaASegmento = (p, a, b) => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const largo2 = dx * dx + dy * dy;
  const t = largo2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / largo2));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
};

const arrastrar = (eventoInicial, elemento, alMover) => {
  eventoInicial.stopPropagation();
  eventoInicial.preventDefault();
  const svg = elemento.ownerSVGElement || elemento;
  const ctmInversa = elemento.getScreenCTM().inverse();
  const mover = (e) => alMover(aCoordenadasFlow(e, ctmInversa, svg));
  const soltar = () => {
    window.removeEventListener("mousemove", mover);
    window.removeEventListener("mouseup", soltar);
  };
  window.addEventListener("mousemove", mover);
  window.addEventListener("mouseup", soltar);
};

const EdgeEditable = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style,
  markerEnd,
  label,
  labelStyle,
  labelBgStyle,
  data = {},
}) => {
  const refEtiqueta = useRef(null);
  const refPrincipal = useRef(null);
  const [pathZonaClic, setPathZonaClic] = useState(null);
  const { puntos = [], etiquetaDx = 0, etiquetaDy = 0, editando, esRetroceso, clave, onCambiar } = data;
  const origen = { x: sourceX, y: sourceY };
  const destino = { x: targetX, y: targetY };

  let path;
  let ancla;
  if (puntos.length > 0) {
    const vertices = [origen, ...puntos, destino];
    path = vertices.map((v, i) => `${i === 0 ? "M" : "L"} ${v.x},${v.y}`).join(" ");
    const mitad = Math.floor((vertices.length - 1) / 2);
    ancla = { x: (vertices[mitad].x + vertices[mitad + 1].x) / 2, y: (vertices[mitad].y + vertices[mitad + 1].y) / 2 };
  } else if (esRetroceso) {
    const topY = Math.min(sourceY, targetY) - ALTURA_ARCO;
    path = `M ${sourceX},${sourceY} L ${sourceX},${topY} L ${targetX},${topY} L ${targetX},${targetY}`;
    ancla = { x: (sourceX + targetX) / 2, y: topY };
  } else {
    const params = { sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition };
    path = getBezierPath(params);
    const [x, y] = getBezierEdgeCenter(params);
    ancla = { x, y };
  }

  useLayoutEffect(() => {
    const principal = refPrincipal.current;
    if (!editando || !principal) return;
    const largo = principal.getTotalLength();
    if (largo <= MARGEN_EXTREMOS * 3) {
      setPathZonaClic(null);
      return;
    }
    const muestras = [];
    for (let d = MARGEN_EXTREMOS; d <= largo - MARGEN_EXTREMOS; d += 6) muestras.push(principal.getPointAtLength(d));
    muestras.push(principal.getPointAtLength(largo - MARGEN_EXTREMOS));
    setPathZonaClic(muestras.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x},${p.y}`).join(" "));
  }, [path, editando]);

  const cambiar = (patch) => onCambiar && onCambiar(clave, patch);

  const agregarPunto = (evento) => {
    if (!editando) return;
    const svg = evento.currentTarget.ownerSVGElement;
    const nuevo = aCoordenadasFlow(evento, evento.currentTarget.getScreenCTM().inverse(), svg);
    const vertices = [origen, ...puntos, destino];
    let mejor = 0;
    let mejorDistancia = Infinity;
    for (let i = 0; i < vertices.length - 1; i += 1) {
      const d = distanciaASegmento(nuevo, vertices[i], vertices[i + 1]);
      if (d < mejorDistancia) {
        mejorDistancia = d;
        mejor = i;
      }
    }
    const siguientes = [...puntos];
    siguientes.splice(mejor, 0, nuevo);
    cambiar({ puntos: siguientes });
  };

  const moverPunto = (indice) => (evento) =>
    arrastrar(evento, evento.currentTarget, (p) => cambiar({ puntos: puntos.map((q, i) => (i === indice ? p : q)) }));

  const quitarPunto = (indice) => (evento) => {
    evento.stopPropagation();
    cambiar({ puntos: puntos.filter((_, i) => i !== indice) });
  };

  const moverEtiqueta = (evento) => {
    if (!editando || !refEtiqueta.current) return;
    const inicio = aCoordenadasFlow(evento, refEtiqueta.current.getScreenCTM().inverse(), refEtiqueta.current.ownerSVGElement);
    const dxInicial = etiquetaDx;
    const dyInicial = etiquetaDy;
    arrastrar(evento, refEtiqueta.current, (p) => cambiar({ etiquetaDx: dxInicial + p.x - inicio.x, etiquetaDy: dyInicial + p.y - inicio.y }));
  };

  const etiquetaX = ancla.x + etiquetaDx;
  const etiquetaY = ancla.y + etiquetaDy;

  return (
    <>
      <path
        ref={refPrincipal}
        id={id}
        className="react-flow__edge-path"
        d={path}
        fill="none"
        strokeLinejoin="round"
        style={editando ? { ...style, pointerEvents: "none" } : style}
        markerEnd={markerEnd}
      />
      {editando && pathZonaClic && (
        <path d={pathZonaClic} fill="none" stroke="transparent" strokeWidth={14} style={{ cursor: "copy", pointerEvents: "stroke" }} onClick={agregarPunto} />
      )}
      {editando &&
        puntos.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={6}
            fill="white"
            stroke={style?.stroke || "#8c8c8c"}
            strokeWidth={2}
            style={{ cursor: "move", pointerEvents: "all" }}
            onMouseDown={moverPunto(i)}
            onDoubleClick={quitarPunto(i)}
          />
        ))}
      {label && (
        <foreignObject
          ref={refEtiqueta}
          x={etiquetaX - 60}
          y={etiquetaY - 12}
          width={120}
          height={24}
          style={{ overflow: "visible", pointerEvents: editando ? "all" : "none" }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              onMouseDown={moverEtiqueta}
              style={{
                fontSize: 11,
                padding: "2px 8px",
                borderRadius: 4,
                display: "inline-block",
                whiteSpace: "nowrap",
                userSelect: "none",
                backgroundColor: "white",
                border: "1px solid #f0f0f0",
                cursor: editando ? "move" : "default",
                ...labelBgStyle,
                ...labelStyle,
              }}
            >
              {label}
            </div>
          </div>
        </foreignObject>
      )}
    </>
  );
};

export default EdgeEditable;
